/**
 * SIGNALS-PRACTICE-LENS-2W frontend practice-lens contracts.
 *
 * A small, framework-free module: parsing/validation for the three wire
 * shapes this slice consumes (list_signal_practices' practice list,
 * get_signal_feed's practice_lens summary, get_signal_detail's
 * practice_classification), plus the pure view-state logic (URL resolution,
 * empty-state copy, basis labels) the components render from. Kept dependency
 * -free from React and from ../types so it is directly testable in Node
 * Vitest with no DOM and no query client.
 *
 * This mirrors the invariants the backend's supabase/functions/_shared/
 * practice-lens.ts already enforces server-side (exact keys, ascending
 * display order, no duplicates, no unknown bases) WITHOUT importing that
 * module: Vite code never imports the Deno backend, so every check here is a
 * restatement, refusing a malformed payload rather than repairing it.
 *
 * The ten-key union below is a closed frontend mirror for exact validation
 * only. It never substitutes for the server: live display names and order
 * come from list_signal_practices, and a key the loaded registry does not
 * currently list is treated as unknown even if it is a member of this union.
 */

// ---------------------------------------------------------------------------
// Canonical key membership (closed frontend mirror)
// ---------------------------------------------------------------------------

export const PRACTICE_KEYS = [
  "te",
  "vc",
  "latam",
  "jvp",
  "tr",
  "pi_healthcare",
  "tas",
  "ocfo",
  "digital",
  "eu",
] as const;

export type PracticeKey = (typeof PRACTICE_KEYS)[number];

/** Exact, case-sensitive membership. No trimming, no lowercasing, no alias
 *  resolution — "TE", " te" and "te " are all refused. */
export function isPracticeKey(value: unknown): value is PracticeKey {
  return typeof value === "string" &&
    (PRACTICE_KEYS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// The practice list (list_signal_practices)
// ---------------------------------------------------------------------------

export interface CanonicalPractice {
  practiceKey: PracticeKey;
  displayName: string;
  displayOrder: number;
}

/**
 * Parses list_signal_practices' `data.practices`. Refuses, rather than
 * repairs: an unknown key, a non-positive/non-safe display order, an
 * out-of-order row or a duplicate key all throw. The caller (the registry
 * query) surfaces the failure as an honest load error — it never falls back
 * to a partial or reordered list.
 */
export function parseCanonicalPractices(rows: unknown): CanonicalPractice[] {
  if (!Array.isArray(rows)) {
    throw new Error("Practice list response was not an array");
  }
  const seen = new Set<string>();
  let previousOrder = 0;
  const practices: CanonicalPractice[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      throw new Error("Practice list row was not an object");
    }
    const r = row as Record<string, unknown>;
    if (!isPracticeKey(r.practice_key)) {
      throw new Error("Practice list row has an unknown practice_key");
    }
    if (typeof r.display_name !== "string") {
      throw new Error("Practice list row has no display_name");
    }
    if (
      typeof r.display_order !== "number" ||
      !Number.isSafeInteger(r.display_order) ||
      r.display_order <= 0
    ) {
      throw new Error("Practice list row has an invalid display_order");
    }
    if (seen.has(r.practice_key)) {
      throw new Error("Practice list returned a duplicate practice_key");
    }
    if (r.display_order <= previousOrder) {
      throw new Error("Practice list returned practices out of order");
    }
    seen.add(r.practice_key);
    previousOrder = r.display_order;
    practices.push({
      practiceKey: r.practice_key,
      displayName: r.display_name,
      displayOrder: r.display_order,
    });
  }
  return practices;
}

// ---------------------------------------------------------------------------
// Effective practice classification (get_signal_detail)
// ---------------------------------------------------------------------------

export const PRACTICE_MEMBERSHIP_BASES = [
  "curator",
  "automated",
  "legacy_automated",
] as const;
export type PracticeMembershipBasis = (typeof PRACTICE_MEMBERSHIP_BASES)[number];

export const AUTOMATED_ROUTING_STATUS = "not_available" as const;

export interface ClassifiedPractice extends CanonicalPractice {
  bases: PracticeMembershipBasis[];
}

export interface PracticeClassification {
  practices: ClassifiedPractice[];
  automatedRouting: typeof AUTOMATED_ROUTING_STATUS;
}

/**
 * Parses get_signal_detail's `practice_classification`. Literal `null` means
 * "unavailable" and passes straight through — it is never repaired into an
 * empty list. `undefined` is NOT the same value: the accepted detail contract
 * reserves `null` for an honest "unavailable" read, so an omitted/undefined
 * field is a malformed response (a missing or stale projection) and is
 * refused rather than silently treated as "unavailable". The caller is
 * responsible for distinguishing "field absent" (a feed row, which legitimately
 * never carries this detail-only field) from "field present as null" (a
 * detail row reporting unavailable) BEFORE calling this parser — see
 * liveAdapters.ts, which only calls this for a detail row whose
 * practice_classification is an own property of the response.
 *
 * Anything else must be a well-formed classification object: an unknown
 * practice or basis, a duplicate, an out-of-order row, an empty bases array,
 * or any `automated_routing` other than "not_available" is refused.
 */
export function parsePracticeClassification(
  raw: unknown,
): PracticeClassification | null {
  if (raw === null) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("practice_classification was not an object or null");
  }
  const obj = raw as Record<string, unknown>;
  if (obj.automated_routing !== AUTOMATED_ROUTING_STATUS) {
    throw new Error(
      "practice_classification.automated_routing was not not_available",
    );
  }
  if (!Array.isArray(obj.practices)) {
    throw new Error("practice_classification.practices was not an array");
  }
  const seen = new Set<string>();
  let previousOrder = 0;
  const practices: ClassifiedPractice[] = [];
  for (const row of obj.practices) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      throw new Error("practice_classification row was not an object");
    }
    const r = row as Record<string, unknown>;
    if (!isPracticeKey(r.practice_key)) {
      throw new Error("practice_classification row has an unknown practice_key");
    }
    if (typeof r.display_name !== "string") {
      throw new Error("practice_classification row has no display_name");
    }
    if (
      typeof r.display_order !== "number" ||
      !Number.isSafeInteger(r.display_order) ||
      r.display_order <= 0
    ) {
      throw new Error("practice_classification row has an invalid display_order");
    }
    if (seen.has(r.practice_key)) {
      throw new Error("practice_classification returned a duplicate practice");
    }
    if (r.display_order <= previousOrder) {
      throw new Error("practice_classification returned practices out of order");
    }
    if (!Array.isArray(r.bases) || r.bases.length === 0) {
      throw new Error("practice_classification row has no bases");
    }
    const basisSeen = new Set<string>();
    const bases: PracticeMembershipBasis[] = [];
    for (const basis of r.bases) {
      if (
        !(PRACTICE_MEMBERSHIP_BASES as readonly string[]).includes(
          basis as string,
        )
      ) {
        throw new Error("practice_classification row has an unknown basis");
      }
      if (basisSeen.has(basis as string)) {
        throw new Error("practice_classification row has a duplicate basis");
      }
      basisSeen.add(basis as string);
      bases.push(basis as PracticeMembershipBasis);
    }
    seen.add(r.practice_key);
    previousOrder = r.display_order;
    practices.push({
      practiceKey: r.practice_key,
      displayName: r.display_name,
      displayOrder: r.display_order,
      bases,
    });
  }
  return { practices, automatedRouting: AUTOMATED_ROUTING_STATUS };
}

const BASIS_LABELS: Record<PracticeMembershipBasis, string> = {
  curator: "Curator assigned",
  automated: "Automated routing",
  legacy_automated: "Legacy automated routing",
};

/** Honest basis label, always in canonical (curator, source-event automated,
 *  then legacy automated) order regardless of the input array's order. */
export function practiceBasisLabel(
  bases: readonly PracticeMembershipBasis[],
): string {
  return PRACTICE_MEMBERSHIP_BASES
    .filter((basis) => bases.includes(basis))
    .map((basis) => BASIS_LABELS[basis])
    .join(" · ");
}

// ---------------------------------------------------------------------------
// The practice lens summary on a filtered feed page (get_signal_feed)
// ---------------------------------------------------------------------------

export interface PracticeLensSummary {
  practiceKey: PracticeKey;
  totalCount: number;
}

/**
 * Parses get_signal_feed's `practice_lens` summary against the practice key
 * the request actually carried. Refuses, rather than repairs, a summary that
 * names a different practice or an invalid total — the caller must never
 * substitute the firm-wide `counts[tab]` for this value.
 */
export function parsePracticeLensSummary(
  raw: unknown,
  requested: PracticeKey,
): PracticeLensSummary {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("practice_lens summary was missing");
  }
  const obj = raw as Record<string, unknown>;
  if (obj.practice_key !== requested) {
    throw new Error("practice_lens summary named another practice");
  }
  if (
    typeof obj.total_count !== "number" ||
    !Number.isSafeInteger(obj.total_count) ||
    obj.total_count < 0
  ) {
    throw new Error("practice_lens summary total was not a count");
  }
  return { practiceKey: requested, totalCount: obj.total_count };
}

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

/**
 * Resolves a `?practice=` URL value against the LOADED canonical registry.
 * Exact, case-sensitive match only — no trim, no lowercase, no alias repair.
 * `null`/empty, an unknown key, or a key the loaded registry does not
 * currently list (inactive, retired, or the registry has not loaded yet) all
 * resolve to `null` (All). The caller must never issue a practice-filtered
 * feed request before the registry has loaded and this has run.
 */
export function resolvePracticeFromUrl(
  value: string | null,
  registry: readonly CanonicalPractice[],
): PracticeKey | null {
  if (!value || !isPracticeKey(value)) return null;
  return registry.some((p) => p.practiceKey === value) ? value : null;
}

// ---------------------------------------------------------------------------
// Empty-state copy
// ---------------------------------------------------------------------------

/** Explicit "nothing routed here yet" copy for a selected practice with zero
 *  rows — never implies that no opportunities exist firm-wide. */
export function practiceEmptyStateCopy(displayName: string): {
  title: string;
  body: string;
} {
  return {
    title: `No signals have been routed to ${displayName} yet.`,
    body: "New signals appear here as they are routed to this practice.",
  };
}
